import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle2, Loader2, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ParsedQuote {
  text: string;
  author: string;
  context?: string;
  year?: number;
  lineNumber?: number;
  errors?: string[];
}

interface FileUploadData {
  file: File;
  id: string;
  status: 'pending' | 'parsing' | 'parsed' | 'error';
  quotes: ParsedQuote[];
  errors: string[];
  progress: number;
}

interface FileUploadComponentProps {
  onQuotesExtracted: (quotes: ParsedQuote[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
}

const SUPPORTED_TYPES = {
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls']
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 10;

export default function FileUploadComponent({ 
  onQuotesExtracted, 
  maxFiles = MAX_FILES, 
  maxFileSize = MAX_FILE_SIZE 
}: FileUploadComponentProps) {
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadData[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string[] => {
    const errors: string[] = [];
    
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${maxFileSize / (1024 * 1024)}MB limit`);
    }
    
    if (!Object.keys(SUPPORTED_TYPES).includes(file.type)) {
      errors.push('Unsupported file type. Please upload .txt, .csv, .xlsx, or .xls files');
    }
    
    return errors;
  };

  const parseTxtFile = async (file: File): Promise<ParsedQuote[]> => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    const quotes: ParsedQuote[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      const errors: string[] = [];
      
      // Try comma delimiter first, then semicolon
      let parts = trimmedLine.split(',').map(part => part.trim().replace(/^["']|["']$/g, ''));
      
      if (parts.length < 2) {
        parts = trimmedLine.split(';').map(part => part.trim().replace(/^["']|["']$/g, ''));
      }

      if (parts.length < 2) {
        errors.push('Invalid format - missing author');
      }

      const quote: ParsedQuote = {
        text: parts[0] || '',
        author: parts[1] || '',
        context: parts[2] || '',
        year: parts[3] ? parseInt(parts[3]) : undefined,
        lineNumber: index + 1,
        errors
      };

      // Validate quote text
      if (!quote.text || quote.text.length < 3) {
        quote.errors?.push('Quote text too short');
      }
      if (quote.text.length > 500) {
        quote.errors?.push('Quote text exceeds 500 characters');
      }

      // Validate author
      if (!quote.author || quote.author.length < 2) {
        quote.errors?.push('Author name too short');
      }

      // Validate year
      if (quote.year && (quote.year < 1000 || quote.year > new Date().getFullYear())) {
        quote.errors?.push('Invalid year');
      }

      quotes.push(quote);
    });

    return quotes;
  };

  const parseCsvFile = async (file: File): Promise<ParsedQuote[]> => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    const quotes: ParsedQuote[] = [];

    // Skip header row if it exists
    const startIndex = lines[0]?.toLowerCase().includes('quote') ? 1 : 0;

    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const errors: string[] = [];
      
      // Simple CSV parsing (handles quoted fields)
      const parts: string[] = [];
      let current = '';
      let inQuotes = false;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          parts.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      parts.push(current.trim());

      if (parts.length < 2) {
        errors.push('Invalid CSV format - missing required columns');
      }

      const quote: ParsedQuote = {
        text: parts[0] || '',
        author: parts[1] || '',
        context: parts[2] || '',
        year: parts[3] ? parseInt(parts[3]) : undefined,
        lineNumber: i + 1,
        errors
      };

      // Validation logic (same as txt)
      if (!quote.text || quote.text.length < 3) {
        quote.errors?.push('Quote text too short');
      }
      if (quote.text.length > 500) {
        quote.errors?.push('Quote text exceeds 500 characters');
      }
      if (!quote.author || quote.author.length < 2) {
        quote.errors?.push('Author name too short');
      }
      if (quote.year && (quote.year < 1000 || quote.year > new Date().getFullYear())) {
        quote.errors?.push('Invalid year');
      }

      quotes.push(quote);
    }

    return quotes;
  };

  const parseExcelFile = async (file: File): Promise<ParsedQuote[]> => {
    // This would require a library like xlsx for full Excel support
    // For now, we'll return an error suggesting CSV conversion
    return [{
      text: '',
      author: '',
      errors: ['Excel file parsing not yet implemented. Please convert to CSV format.']
    }];
  };

  const parseFile = async (file: File): Promise<ParsedQuote[]> => {
    switch (file.type) {
      case 'text/plain':
        return parseTxtFile(file);
      case 'text/csv':
        return parseCsvFile(file);
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      case 'application/vnd.ms-excel':
        return parseExcelFile(file);
      default:
        throw new Error('Unsupported file type');
    }
  };

  const processFile = async (file: File) => {
    const fileId = Math.random().toString(36).substring(7);
    const validationErrors = validateFile(file);

    const fileData: FileUploadData = {
      file,
      id: fileId,
      status: validationErrors.length > 0 ? 'error' : 'parsing',
      quotes: [],
      errors: validationErrors,
      progress: 0
    };

    setUploadedFiles(prev => [...prev, fileData]);

    if (validationErrors.length > 0) {
      return;
    }

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadedFiles(prev => 
          prev.map(f => f.id === fileId ? { ...f, progress: Math.min(f.progress + 10, 90) } : f)
        );
      }, 100);

      const quotes = await parseFile(file);
      
      clearInterval(progressInterval);
      
      setUploadedFiles(prev => 
        prev.map(f => f.id === fileId ? { 
          ...f, 
          status: 'parsed', 
          quotes, 
          progress: 100 
        } : f)
      );
    } catch (error) {
      setUploadedFiles(prev => 
        prev.map(f => f.id === fileId ? { 
          ...f, 
          status: 'error', 
          errors: [error instanceof Error ? error.message : 'Failed to parse file'],
          progress: 0
        } : f)
      );
    }
  };

  const handleFiles = useCallback((files: FileList) => {
    if (uploadedFiles.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    Array.from(files).forEach(processFile);
  }, [uploadedFiles.length, maxFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const getAllValidQuotes = (): ParsedQuote[] => {
    return uploadedFiles
      .flatMap(file => file.quotes)
      .filter(quote => !quote.errors || quote.errors.length === 0);
  };

  const handleSubmit = () => {
    const validQuotes = getAllValidQuotes();
    if (validQuotes.length > 0) {
      onQuotesExtracted(validQuotes);
    }
  };

  const totalQuotes = uploadedFiles.reduce((sum, file) => sum + file.quotes.length, 0);
  const validQuotes = getAllValidQuotes();
  const hasErrors = uploadedFiles.some(file => file.status === 'error' || file.quotes.some(q => q.errors && q.errors.length > 0));

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-brand-500 bg-brand-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={() => setDragActive(true)}
        onDragLeave={() => setDragActive(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <div className="space-y-2">
          <p className="text-lg font-medium text-gray-900">
            Drop files here or click to upload
          </p>
          <p className="text-sm text-gray-500">
            Supports .txt, .csv, .xlsx, .xls files up to {maxFileSize / (1024 * 1024)}MB
          </p>
          <p className="text-xs text-gray-400">
            Maximum {maxFiles} files per upload
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.csv,.xlsx,.xls"
          onChange={handleChange}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mt-4 btn-primary"
        >
          Select Files
        </button>
      </div>

      {/* File List */}
      <AnimatePresence>
        {uploadedFiles.map((fileData) => (
          <motion.div
            key={fileData.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="card p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">{fileData.file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(fileData.file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={() => removeFile(fileData.id)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress Bar */}
            {fileData.status === 'parsing' && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
                  <span className="text-sm text-gray-600">Parsing file...</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-brand-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${fileData.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-2 mb-3">
              {fileData.status === 'parsed' && (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-700">
                    {fileData.quotes.length} quotes found
                  </span>
                </>
              )}
              {fileData.status === 'error' && (
                <>
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-700">Error parsing file</span>
                </>
              )}
            </div>

            {/* Errors */}
            {fileData.errors.length > 0 && (
              <div className="mb-3 p-3 bg-red-50 rounded-md">
                <h4 className="text-sm font-medium text-red-800 mb-1">Errors:</h4>
                <ul className="text-sm text-red-700 list-disc list-inside">
                  {fileData.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quote Preview */}
            {fileData.quotes.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700"
                >
                  <Eye className="h-4 w-4" />
                  Preview quotes ({fileData.quotes.length})
                </button>
                
                {showPreview && (
                  <div className="mt-3 max-h-60 overflow-y-auto border rounded-md">
                    {fileData.quotes.slice(0, 5).map((quote, index) => (
                      <div key={index} className="p-3 border-b last:border-b-0">
                        <p className="text-sm">
                          <strong>Quote:</strong> {quote.text.substring(0, 100)}
                          {quote.text.length > 100 && '...'}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Author:</strong> {quote.author}
                        </p>
                        {quote.context && (
                          <p className="text-sm text-gray-600">
                            <strong>Context:</strong> {quote.context}
                          </p>
                        )}
                        {quote.year && (
                          <p className="text-sm text-gray-600">
                            <strong>Year:</strong> {quote.year}
                          </p>
                        )}
                        {quote.errors && quote.errors.length > 0 && (
                          <p className="text-sm text-red-600">
                            <strong>Errors:</strong> {quote.errors.join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                    {fileData.quotes.length > 5 && (
                      <div className="p-3 text-center text-sm text-gray-500">
                        ... and {fileData.quotes.length - 5} more quotes
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Summary and Submit */}
      {uploadedFiles.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Upload Summary</h3>
              <p className="text-sm text-gray-600">
                {totalQuotes} total quotes found, {validQuotes.length} valid quotes ready for import
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setUploadedFiles([])}
                className="btn-secondary"
              >
                Clear All
              </button>
              <button
                onClick={handleSubmit}
                disabled={validQuotes.length === 0}
                className="btn-primary disabled:opacity-50"
              >
                Import {validQuotes.length} Quotes
              </button>
            </div>
          </div>
          
          {hasErrors && (
            <div className="p-3 bg-yellow-50 rounded-md">
              <p className="text-sm text-yellow-800">
                Some quotes have validation errors and will be skipped during import.
                Please review the errors above.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Format Guide */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Supported File Formats</h3>
        <div className="space-y-4 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-900">Text Files (.txt)</h4>
            <p>One quote per line, comma-separated format:</p>
            <code className="block bg-gray-100 p-2 rounded mt-1">
              "Quote text", Author Name, Context, Year
            </code>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">CSV Files (.csv)</h4>
            <p>Standard CSV with headers: quote, author, context, year</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Excel Files (.xlsx, .xls)</h4>
            <p>Currently not supported. Please convert to CSV format.</p>
          </div>
        </div>
      </div>
    </div>
  );
}